/**
 * printCalculationDocument
 * ------------------------------------------------------------------
 * מנגנון הדפסה נקי ל-CarTax (מסמך A4 RTL ייעודי).
 *
 * גישה: משכפלים את אזור התוצאה לתוך <iframe> נסתר ומבודד, ממירים שדות
 * טופס חיים לטקסט, מסירים כפתורים/ניווט/רכיבים מסומנים, ומדפיסים את ה-iframe
 * בלבד. ה-iframe מבודד לחלוטין → אין בליעה מ-index.css ואין דליפת DOM.
 *
 * חשוב (מובייל): מסירים את ה-iframe רק ב-afterprint (סגירת הדיאלוג), לא
 * בטיימר קבוע — אחרת במובייל ה-iframe נמחק לפני שמירת ה-PDF וההדפסה "נופלת"
 * לעמוד הראשי. *לא* מאזינים ל-focus/visibilitychange — במובייל הם נורים כבר
 * בפתיחת הדיאלוג ומשחזרים מוקדם מדי.
 */

export interface PrintDocumentMeta {
  /** שם המחשבון — כותרת ראשית */
  calculatorName?: string;
  /** תאריך החישוב — בפינת הכותרת */
  calculationDate?: string;
  /** תת-כותרת אופציונלית */
  subtitle?: string;
  /**
   * סוג מקור ה-DOM:
   *  - "history" (ברירת מחדל): כרטיסי bg-white + טבלאות (דף ההיסטוריה).
   *  - "calculator": כרטיסים צבעוניים + שורות flex (דף תוצאה במחשבון).
   */
  variant?: "history" | "calculator";
}

/** מנקה תווי HTML מטקסט שמוזרק לכותרת */
function escapeHtml(str: string): string {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** בונה את ה-HTML המלא של מסמך ההדפסה (מסמך עצמאי בתוך iframe/טאב).
 *  withPrintBar=true (מובייל בלבד) מוסיף סרגל כפתור הדפסה + auto-print. */
function buildPrintHtml(
  bodyHtml: string,
  meta: PrintDocumentMeta,
  withPrintBar = false
): string {
  const title = meta.calculatorName || "תוצאות חישוב";
  const subtitle = meta.subtitle || "פירוט חישוב מיסי ייבוא רכב";
  const date = meta.calculationDate || "";
  const docClass = meta.variant === "calculator" ? "doc doc--calculator" : "doc";

  return `<!DOCTYPE html>
<html lang="he" dir="rtl">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)} — CarTax</title>
<style>
  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

  @page { size: A4; margin: 14mm 12mm; }

  html, body {
    background: #ffffff;
    color: #1f2937;
    font-family: 'Assistant', 'Heebo', 'Rubik', 'Segoe UI', Arial, sans-serif;
    direction: rtl;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  body { font-size: 12.5px; line-height: 1.55; }

  .doc { max-width: 780px; margin: 0 auto; }

  /* ===== כותרת מסמך ===== */
  .doc-header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 14px;
    margin-bottom: 24px;
    border-bottom: 2px solid #1d4ed8;
  }
  .doc-title { font-size: 21px; font-weight: 800; color: #0f172a; letter-spacing: -0.01em; }
  .doc-sub { font-size: 12px; color: #64748b; margin-top: 3px; }
  .doc-brand {
    font-size: 26px; font-weight: 800; color: #2563eb;
    letter-spacing: -0.02em; line-height: 1; text-align: left; white-space: nowrap;
  }
  .doc-date { text-align: left; font-size: 11px; color: #94a3b8; margin-top: 6px; }

  /* ===== איפוס כרטיסים ===== */
  .doc .bg-white {
    background: #fff !important; box-shadow: none !important; border: none !important;
    border-radius: 0 !important; padding: 0 !important; margin: 0 0 18px !important;
    page-break-inside: avoid; break-inside: avoid;
  }
  .doc h1 { display: none; }
  .doc h2 {
    font-size: 14px; font-weight: 700; color: #0f172a;
    margin: 0 0 10px; padding-bottom: 6px; border-bottom: 1px solid #e2e8f0;
  }
  .doc h3, .doc h4 { font-size: 12.5px; font-weight: 700; color: #334155; margin: 12px 0 6px; }
  .doc p { margin: 0 0 4px; }

  /* ===== טבלאות ===== */
  .doc table { width: 100%; border-collapse: collapse; }
  .doc td { padding: 7px 2px; border-bottom: 1px solid #f1f5f9; vertical-align: top; text-align: right; }
  .doc td:first-child { color: #64748b; }
  .doc td:last-child, .doc td.text-left { text-align: left; font-weight: 600; color: #0f172a; }
  .doc tr:last-child td { border-bottom: none; }

  /* ===== פנלים מודגשים ===== */
  .doc [class*="rounded"] { border-radius: 8px; }
  .doc .bg-gray-50 { background: #f8fafc !important; padding: 12px !important; border-radius: 8px; }
  .doc .bg-blue-50 { background: #f6f9ff !important; padding: 12px !important; border: 1px solid #e6edfb; margin-bottom: 12px; }
  .doc .bg-blue-100 { background: #eef4ff !important; padding: 12px !important; border: 1px solid #dbe7ff; }
  .doc .bg-yellow-50 {
    background: #f7faff !important; padding: 14px !important;
    border: 1px solid #e2e8f0 !important; border-right: 3px solid #1d4ed8 !important; margin-bottom: 14px;
  }
  .doc tr.bg-gray-100 td { background: #f1f5f9 !important; font-weight: 700; border-bottom: none; }
  .doc tr.bg-blue-200 td { background: #e4edff !important; font-weight: 700; border-bottom: none; }
  .doc tr.bg-orange-50 td { background: #fff7ed !important; }
  .doc tr.bg-blue-100 td { background: #eef4ff !important; }

  /* ===== צבעים סמנטיים ===== */
  .doc .text-green-700 { color: #15803d !important; }
  .doc .text-red-700 { color: #b91c1c !important; }
  .doc .text-orange-600 { color: #c2410c !important; }
  .doc .text-blue-600, .doc .text-blue-700, .doc .text-blue-800, .doc .text-blue-900 { color: #1d4ed8 !important; }
  .doc .text-gray-800 { color: #1f2937 !important; }
  .doc .text-gray-700 { color: #374151 !important; }
  .doc .text-gray-600 { color: #4b5563 !important; }
  .doc .text-gray-500 { color: #6b7280 !important; }

  /* הערת שנת המס (TaxYearNotice): צבע/משקל/גודל מגיעים ממיפוי ה-Tailwind למעלה ולמטה */
  .doc .tax-year-notice { margin: 0 0 12px; }

  /* ערכי שדות טופס שהומרו לטקסט (דייק לי / אבזור) */
  .doc .print-field-value { display: inline-block; font-weight: 700; color: #0f172a; padding: 2px 0; }

  .doc .font-bold { font-weight: 700 !important; }
  .doc .font-medium { font-weight: 600 !important; }
  .doc .text-xs { font-size: 12px !important; }
  .doc .text-lg, .doc .text-xl, .doc .text-2xl { font-size: 15px !important; }
  .doc .text-3xl { font-size: 17px !important; }

  /* ===== variant: calculator ===== */
  .doc--calculator .doc-body > div {
    background: #fff !important; border: none !important; border-radius: 0 !important;
    padding: 0 !important; margin: 0 0 18px !important; page-break-inside: avoid; break-inside: avoid;
  }
  .doc--calculator .grid { display: block !important; }
  /* שורות label/value (flex של המחשבון + שדות "דייק לי" שהומרו) */
  .doc--calculator .flex.justify-between,
  .doc--calculator .grid > div {
    display: flex !important; justify-content: space-between; align-items: baseline;
    gap: 12px; padding: 6px 2px; border-bottom: 1px solid #f1f5f9 !important;
  }
  .doc--calculator .flex.justify-between > span:first-child,
  .doc--calculator .grid > div > label {
    color: #64748b; font-weight: 600; margin: 0; display: inline;
  }
  .doc--calculator .flex.justify-between > span:last-child,
  .doc--calculator .grid > div > .print-field-value {
    color: #0f172a; font-weight: 700; text-align: left; flex: 0 0 auto;
  }

  /* עזרי flex */
  .doc .flex { display: flex; }
  .doc .items-center { align-items: center; }
  .doc .justify-between { justify-content: space-between; }
  .doc .gap-2 { gap: 8px; }

  /* ===== כותרת תחתונה ===== */
  .doc-footer {
    margin-top: 26px; padding-top: 12px; border-top: 1px solid #e2e8f0;
    text-align: center; font-size: 10.5px; color: #94a3b8; line-height: 1.7;
  }
  .doc-footer strong { color: #64748b; font-weight: 700; }

  /* ===== סרגל הדפסה (מובייל בלבד — window.open) ===== */
  .print-bar {
    position: sticky; top: 0; z-index: 999;
    display: flex; align-items: center; justify-content: center; gap: 12px;
    background: #1d4ed8; padding: 12px 16px;
    box-shadow: 0 2px 10px rgba(15, 23, 42, 0.18);
  }
  .print-bar button {
    background: #ffffff; color: #1d4ed8; border: none; border-radius: 8px;
    font-family: inherit; font-size: 15px; font-weight: 800;
    padding: 11px 26px; cursor: pointer;
  }
  /* כפתור "סגור וחזור" — בלעדיו ספארי ב-iPad/iPhone משאיר את המשתמש תקוע:
     הטאב נפתח כ-about:blank, אין לו היסטוריה, וחץ ה-back מושבת. */
  .print-bar button.secondary {
    background: transparent; color: #ffffff; font-weight: 700;
    border: 1.5px solid rgba(255, 255, 255, 0.7); padding: 10px 18px;
  }
  /* מוצג רק אם window.close() נחסם */
  .print-hint {
    display: none; margin: 14px auto 0; max-width: 780px;
    background: #f1f5f9; color: #334155; border-radius: 8px;
    padding: 12px 14px; font-size: 13px; text-align: center;
  }
  @media print { .print-bar, .print-hint { display: none !important; } }
</style>
</head>
<body>
  ${
    withPrintBar
      ? `<div class="print-bar"><button type="button" onclick="window.print()">🖨️ הדפס / שמור כ-PDF</button><button type="button" class="secondary" onclick="closePrintTab()">✕ סגור וחזור</button></div>
  <div class="print-hint" id="print-hint">כדי לחזור ל-CarTax: פתחו את רשימת הלשוניות בדפדפן ובחרו בלשונית של האתר.</div>`
      : ""
  }
  <div class="${docClass}">
    <div class="doc-header">
      <div class="titles">
        <div class="doc-title">${escapeHtml(title)}</div>
        <div class="doc-sub">${escapeHtml(subtitle)}</div>
      </div>
      <div>
        <div class="doc-brand">CarTax</div>
        ${date ? `<div class="doc-date">תאריך: ${escapeHtml(date)}</div>` : ""}
      </div>
    </div>

    <div class="doc-body">${bodyHtml}</div>

    <div class="doc-footer">
      <div><strong>CarTax</strong> · מחשבון מיסי ייבוא רכב לישראל · www.cartax.org</div>
      <div>השימוש במחשבון כפוף לתקנות המכס הישראליות. החישוב הוא אומדן בלבד ואינו מהווה ייעוץ או התחייבות.</div>
    </div>
  </div>
  ${
    withPrintBar
      ? `<script>window.closePrintTab=function(){try{window.close();}catch(e){}setTimeout(function(){var h=document.getElementById("print-hint");if(h)h.style.display="block";},600);};window.addEventListener("load",function(){var u=navigator.userAgent;var ios=/iPad|iPhone|iPod/.test(u)||(/Macintosh/.test(u)&&(navigator.maxTouchPoints||0)>1);if(ios)return;setTimeout(function(){try{window.print();}catch(e){}},350);});</script>`
      : ""
  }
</body>
</html>`;
}

/**
 * מעבד את אלמנט המקור: שכפול, המרת שדות טופס לטקסט, והסרת רכיבים שאינם
 * רלוונטיים להדפסה. מחזיר את ה-innerHTML המעובד.
 */
function processSource(source: HTMLElement, meta: PrintDocumentMeta): string {
  const clone = source.cloneNode(true) as HTMLElement;

  // המרת שדות טופס חיים לטקסט — קוראים את ה-value ה*חי* מהמקור (cloneNode
  // אינו מעתיק value חי). הסדר זהה בין source ל-clone → מיפוי לפי אינדקס.
  const srcControls = Array.from(
    source.querySelectorAll("input, textarea, select")
  ) as Array<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;
  const cloneControls = Array.from(
    clone.querySelectorAll("input, textarea, select")
  );
  cloneControls.forEach((node, i) => {
    const src = srcControls[i];
    const span = document.createElement("span");
    span.className = "print-field-value";
    if (!src) {
      span.textContent = "";
    } else if (
      src instanceof HTMLInputElement &&
      (src.type === "checkbox" || src.type === "radio")
    ) {
      span.textContent = src.checked ? "✓" : "—";
    } else if (src instanceof HTMLSelectElement) {
      const opt = src.selectedOptions && src.selectedOptions[0];
      span.textContent = opt ? opt.textContent || "" : "";
    } else {
      span.textContent = (src as HTMLInputElement).value || "—";
    }
    node.replaceWith(span);
  });

  // הסרת רכיבים שאינם להדפסה (כפתורים, ניווט, ורכיבים מסומנים כגון כרטיס
  // השמירה/הערות וטריגר ה-tooltip "?").
  clone
    .querySelectorAll(
      "button, nav, .print\\:hidden, .print-exclude, [data-print-exclude]"
    )
    .forEach((el) => el.remove());

  // הסרת כרטיס הכותרת המקורי (הראשון) — רלוונטי רק לדף ההיסטוריה.
  if (meta.variant !== "calculator") {
    const firstCard = clone.querySelector(".bg-white");
    if (firstCard) firstCard.remove();
  }

  // חשיפת אלמנטים שמיועדים להדפסה בלבד (footer מקורי וכו')
  clone
    .querySelectorAll(".hidden.print\\:block")
    .forEach((el) => ((el as HTMLElement).style.display = "block"));

  return clone.innerHTML;
}

/**
 * זיהוי דפדפן מובייל. בדפדפני מובייל (אנדרואיד/iOS) הדפסת iframe נסתר אינה
 * מכובדת — הדפדפן מתעלם מה-subframe ומדפיס את העמוד הראשי החי. שם משתמשים
 * במסלול window.open (טאב עצמאי) במקום.
 */
function isMobileBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  if (/Android|iPhone|iPad|iPod|Mobile/i.test(ua)) return true;
  // iPadOS 13+ מדווח UA של דסקטופ ("Macintosh") — מזוהה לפי מסך מגע מרובה נקודות
  if (/Macintosh/i.test(ua) && (navigator.maxTouchPoints || 0) > 1) return true;
  return false;
}

/**
 * מסלול דסקטופ: מדפיס את המסמך הנקי דרך iframe מבודד 0×0 (עובד מושלם בדסקטופ).
 */
function printViaIframe(source: HTMLElement, meta: PrintDocumentMeta): void {
  const html = buildPrintHtml(processSource(source, meta), meta);

  // iframe נסתר 0×0 (גודל שנותן תצוגה מקדימה תקינה גם במובייל)
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);

  const win = iframe.contentWindow;
  const doc = iframe.contentWindow?.document;
  if (!win || !doc) {
    iframe.remove();
    window.print();
    return;
  }

  doc.open();
  doc.write(html);
  doc.close();

  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  };

  let printed = false;
  const triggerPrint = () => {
    if (printed) return;
    printed = true;
    try {
      win.focus();
      // מסירים את ה-iframe רק אחרי סגירת הדיאלוג — כך הוא חי לאורך כל
      // זרימת התצוגה/שמירה במובייל. *בלי* focus/visibilitychange (יורים
      // מוקדם מדי במובייל ומשחזרים לפני שההדפסה רונדרה).
      win.onafterprint = cleanup;
      win.print();
    } catch (err) {
      console.error("Print error:", err);
      cleanup();
    }
    // רשת ביטחון אם afterprint לא נורה.
    setTimeout(cleanup, 60000);
  };

  // השהיה קצרה כדי לוודא שתוכן ה-iframe רונדר לפני ההדפסה.
  setTimeout(triggerPrint, 250);
}

/**
 * מדפיס את אזור התוצאה כמסמך נקי.
 *  - דסקטופ: iframe מבודד (ללא שינוי).
 *  - מובייל: פותח את המסמך הנקי כעמוד עצמאי בטאב חדש (אנדרואיד/iOS לא מכבדים
 *    הדפסת iframe נסתר ומדפיסים את העמוד הראשי).
 * @param source אלמנט ה-DOM שמכיל את אזור התוצאה (ה-ref)
 * @param meta   מטא-דאטה לכותרת
 */
export function printCalculationDocument(
  source: HTMLElement | null,
  meta: PrintDocumentMeta = {}
): void {
  if (!source) {
    window.print();
    return;
  }

  // מובייל: פותחים את המסמך הנקי בטאב חדש. window.open חייב להיקרא סינכרונית
  // בתוך ה-user-gesture (onClick) כדי לא להיחסם ע"י popup-blocker.
  if (isMobileBrowser()) {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      // הטאב נחסם — נפילה אלגנטית חזרה למסלול ה-iframe.
      printViaIframe(source, meta);
      return;
    }
    const html = buildPrintHtml(processSource(source, meta), meta, true);
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    return;
  }

  // דסקטופ: iframe מבודד.
  printViaIframe(source, meta);
}

export default printCalculationDocument;
